import { useEffect, useState } from 'react'

import {
  Box,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
  useMediaQuery
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useMappedTranslation, useToast } from 'hooks'
import { Button } from 'oneui'

import IconArrowDown from './assets/images/arrowDown.svg'
import IconArrowUp from './assets/images/arrowUp.svg'
import CookieList from './assets/json/cookie-list.json'

type Props = {
  isDialogOpen: boolean
  handleSlide: (boolean) => void
  handleDialog: (boolean) => void
  handleSaEnableCookie: (boolean) => void
}

export default function CookieAgreementDialog({ isDialogOpen, handleSlide, handleDialog, handleSaEnableCookie }: Props) {
  const t = useMappedTranslation()
  const theme = useTheme()
  const toast = useToast()
  const [isEssentialExtend, setIsEssentialExtend] = useState<boolean>(false)

  const handleContinueButton = () => {
    handleSaEnableCookie(true)
    handleDialog(false)
    handleSlide(false)
    toast(t('iam.01.Common.Text.You.have.successfully.updated.your.cookie.preferences'))
  }

  const handleCancelButton = () => {
    handleDialog(false)
  }

  useEffect(() => {
    setIsEssentialExtend(false)
  }, [isDialogOpen])

  return (
    <Dialog
      open={isDialogOpen}
      onClose={handleCancelButton}
      fullScreen={useMediaQuery(theme.breakpoints.down('md'))}
      PaperProps={{
        sx: {
          borderRadius: '26px',
          border: '0',
          maxWidth: '500px',
          maxHeight: { xs: '100%', md: '544px' },
          height: { xs: '100%', md: 'fit-content' }
        }
      }}
    >
      <DialogTitle sx={{ pb: '16px', fontSize: '24px', lineHeight: '28px', color: 'text.primary', fontWeight: '500' }}>
        {t('cookie.agreement.dialog.title')}
      </DialogTitle>
      <DialogContent sx={{ pb: '0', maxHeight: { xs: '100%', md: '378px' } }}>
        <Box sx={{ width: '100%', height: '100%' }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant='body1'>{t('cookie.agreement.dialog.description')}</Typography>
          </Box>
          <Box sx={{ width: '100%', marginTop: '30px' }}>
            <Box sx={{ width: '100%' }}>
              <Typography variant='body2' sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
                {t('cookie.agreement.dialog.essential.title')}
              </Typography>
            </Box>
            <Box sx={{ width: '100%', marginTop: { xs: '12px', md: '10px' } }}>
              <Typography variant='body1'>{t('cookie.agreement.dialog.essential.description')}</Typography>
            </Box>
            <Box sx={{ width: '100%', marginTop: '8px', lineHeight: '20px' }}>
              <Button
                variant='text'
                endIcon={
                  <Box
                    component='img'
                    alt=''
                    src={isEssentialExtend ? IconArrowUp : IconArrowDown}
                    sx={{ width: '20px', height: '20px', margin: '0' }}
                  />
                }
                onClick={() => setIsEssentialExtend(!isEssentialExtend)}
                sx={{
                  padding: 0,
                  color: theme.palette.mode === 'dark' ? '#A9A9A9' : '#505050',
                  minHeight: '20px',
                  maxHeight: '20px',
                  minWidth: '0',
                  lineHeight: '20px',
                  columnGap: '2px',
                  '& .MuiButton-endIcon': {
                    margin: '0'
                  },
                  '&:focus-visible': {
                    outlineOffset: '-2px'
                  }
                }}
                data-testid='Button9D59F960'
              >
                <Typography
                  sx={{
                    color: theme.palette.mode === 'dark' ? '#A9A9A9' : '#505050',
                    lineHeight: '20px',
                    fontSize: '16px',
                    fontWeight: '700'
                  }}
                >
                  {isEssentialExtend ? t('cookie.agreement.dialog.hide.button') : t('cookie.agreement.dialog.view.button')}
                </Typography>
              </Button>
            </Box>
            <Collapse in={isEssentialExtend}>
              <List sx={{ padding: '12px 0' }}>
                {CookieList.essentialCookies.map((cookie, index) => (
                  <Box key={cookie.name}>
                    <ListItem sx={{ padding: '14px 0' }}>
                      <ListItemText
                        sx={{ margin: '0' }}
                        primary={<Typography variant='body1'>{cookie.name}</Typography>}
                        secondary={
                          <Typography variant='body1' className='off' sx={{ color: 'secondary.light' }}>
                            {cookie.domain}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {CookieList.essentialCookies.length === index + 1 ? '' : <Divider />}
                  </Box>
                ))}
              </List>
            </Collapse>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          pt: '30px',
          flexDirection: { xs: 'column-reverse', md: 'row' },
          gap: { xs: '8px', md: '0' }
        }}
      >
        <Button
          variant='text'
          onClick={handleCancelButton}
          sx={{ minHeight: '36px', maxHeight: '36px' }}
          data-testid='ButtonA9DF5672'
        >
          <Typography sx={{ fontSize: '16px', lineHeight: '20px', fontWeight: 700, color: 'inherit' }}>
            {t('cookie.agreement.dialog.cancel.button')}
          </Typography>
        </Button>
        <Divider orientation='vertical' flexItem sx={{ margin: { xs: '0 !important', md: '9px 0 !important' } }} />
        <Button
          variant='text'
          onClick={handleContinueButton}
          sx={{ minHeight: '36px', maxHeight: '36px' }}
          data-testid='Button642A57B4'
        >
          <Typography sx={{ fontSize: '16px', lineHeight: '20px', fontWeight: 700, color: 'inherit' }}>
            {t('cookie.agreement.dialog.approve.button')}
          </Typography>
        </Button>
      </DialogActions>
    </Dialog>
  )
}
